import { css } from 'aphrodite';
import * as React from 'react';
import { TicketStatus } from '../../../models/Support/SupportEnums';
import {
	toAgentTicketCountMap,
	UNASSIGNED_COUNT_KEY,
	useSupportAgentTicketCountsQuery,
} from '../../../queries/Support/useSupportAgentTicketCounts';
import { useSupportCategoriesQuery } from '../../../queries/Support/useSupportCategories';
import { useSupportTeamQuery } from '../../../queries/Support/useSupportTeam';
import {
	useAssignTicketMutation,
	useUpdateTicketCategoryMutation,
	useUpdateTicketStatusMutation,
} from '../../../queries/Support/useSupportTicketMutations';
import { useSupportTicketQuery } from '../../../queries/Support/useSupportTicketQuery';
import { getInitials, getSupportContactProfilePicSrc } from '../../../utils/Support/supportHelpers';
import { Avatar } from '@HoudiniUI/Avatar';
import { ChevronDown } from '../../../web/components/svgs/icons/ChevronDown';
import { MoreIcon } from '../../../web/components/svgs/icons/MoreIcon';
import { SparklesIcon } from '../../../web/components/svgs/icons/SparklesIcon';
import { Button } from '../../../web/components-v2/Button';
import { Drawer, DrawerContent, DrawerHeader, DrawerTitle } from '../../../web/components-v2/Drawer';
import { Dropdown, DropdownContent, DropdownItem, DropdownTrigger } from '../../../web/components-v2/Dropdown';
import { Pill } from '../../../web/components-v2/Pill';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../../web/components-v2/Select';
import { AdminAppAssistantChat } from '../../../web/containers/templates/AssistantChat';
import { ConfirmModal } from '../../components/supportShared/ConfirmModal';
import { TicketChannelBadge } from '../../components/supportShared/TicketChannelBadge';
import { CategoryPicker } from './CategoryPicker';
import { ConversationPane } from './ConversationPane';
import { agentTicketCountHint } from './agentTicketCountHint';
import { CheckGlyph, ReopenGlyph } from './inboxIcons';
import { inboxStyles as s } from './styles/inboxStyles';
import { phoneTicketStyles as p } from './styles/phoneTicketStyles';
import { getStatusInfo, getTicketContactLabel } from './supportTicketPresentation';
import { TicketDetailsBody } from './TicketDetailsBody';
import { ticketMutationFeedback as feedback } from './ticketMutationFeedback';
import { useSupportTicketActivityVisibility } from './useSupportTicketActivityVisibility';

interface PhoneTicketStageProps {
	ticketId: string;
	autoOpenComposer?: boolean;
	targetThreadId?: string;
	/** Navigate the inbox to another ticket from the details sheet's previous-conversations preview. */
	onSelectTicket: (ticketId: string) => void;
	/** Opens the customer picker for customerless tickets. */
	onChangeCustomer: () => void;
	/** Called after the ticket leaves the agent's plate (closed, spam, archived, or assigned) — parent advances. */
	onTicketLeftView: () => void;
}

type PhoneSheet = 'details' | 'assistant';

/**
 * The phone ticket screen. Desktop keeps the persistent details drawer and its icon rail; a phone
 * gets a context header (identity + the primary action), a strip holding the two fields agents
 * actually change on a phone, and bottom sheets for the full details panel and the assistant. That
 * leaves the conversation the full screen width — the rail used to float over it.
 */
export const PhoneTicketStage: React.FC<PhoneTicketStageProps> = ({
	ticketId,
	autoOpenComposer,
	targetThreadId,
	onSelectTicket,
	onChangeCustomer,
	onTicketLeftView,
}) => {
	const [openSheet, setOpenSheet] = React.useState<PhoneSheet | null>(null);
	const [forwardSignal, setForwardSignal] = React.useState(0);
	const [showReopenConfirm, setShowReopenConfirm] = React.useState(false);
	const [showTicketActivity, setShowTicketActivity] = useSupportTicketActivityVisibility();

	React.useEffect(() => {
		setOpenSheet(null);
		setShowReopenConfirm(false);
	}, [ticketId]);

	const { data: ticket } = useSupportTicketQuery(ticketId);
	const updateStatus = useUpdateTicketStatusMutation(ticketId);
	const assignTicket = useAssignTicketMutation(ticketId);
	const updateCategory = useUpdateTicketCategoryMutation(ticketId);

	const { data: teamData } = useSupportTeamQuery(ticket?.mailboxId || '');
	const teamMembers = React.useMemo(
		() => (teamData?.values ?? []).map(agent => ({ id: agent.agentId, name: agent.agentName || agent.agentId })),
		[teamData]
	);
	// Keyed on the ticket's own desk, not the one selected in the inbox: the assignee list comes from
	// that desk's roster, so its counts are the only ones the picker can be read against.
	const { data: agentTicketCountsData } = useSupportAgentTicketCountsQuery(ticket?.mailboxId || '');
	const agentTicketCounts = React.useMemo(() => toAgentTicketCountMap(agentTicketCountsData), [agentTicketCountsData]);
	const categoriesQuery = useSupportCategoriesQuery(ticket?.mailboxId || '');
	const categories = categoriesQuery.data?.values ?? [];

	const status = ticket ? getStatusInfo(ticket.status) : undefined;
	const isClosed = ticket?.status === TicketStatus.Closed;
	const contactLabel = ticket ? getTicketContactLabel(ticket) : '';
	const assigneeName = teamMembers.find(agent => agent.id === ticket?.assigneeId)?.name;
	const activityToggleLabel = showTicketActivity ? 'Hide activity' : 'Show activity';
	const customerActionLabel = ticket?.contactEmail?.trim() ? 'Change customer' : 'Add customer';

	return (
		<div className={css(s.phoneConversationStage)}>
			{ticket && status && (
				<>
					<div className={css(p.header)}>
						<div className={css(p.headerTopRow)}>
							<span className={css(p.ticketNumber)}>#{ticket.number}</span>
							<Pill variant='tag' color={status.color} size='small' label={status.label} />
							<TicketChannelBadge channel={ticket.channel} />
							<span className={css(p.headerSpacer)} />
							<Button
								type='button'
								variant='primary'
								styles={[p.primaryAction]}
								isLoading={updateStatus.isPending}
								title={isClosed ? 'Reopen' : 'Close'}
								onClick={() => {
									if (isClosed) {
										setShowReopenConfirm(true);
										return;
									}
									updateStatus.mutate(TicketStatus.Closed, feedback('Failed to close ticket.', onTicketLeftView));
								}}
							>
								{isClosed ? <ReopenGlyph size={16} /> : <CheckGlyph size={16} />}
								{isClosed ? 'Reopen' : 'Close'}
							</Button>
							<Dropdown>
								<DropdownTrigger
									render={
										<Button type='button' variant='ghost' styles={[p.overflowButton]} aria-label='Ticket actions' />
									}
								>
									<MoreIcon fillColor='currentColor' />
								</DropdownTrigger>
								<DropdownContent align='end'>
									<DropdownItem onClick={() => setOpenSheet('assistant')}>AI Assistant</DropdownItem>
									<DropdownItem onClick={() => setForwardSignal(value => value + 1)}>Forward</DropdownItem>
									<DropdownItem onClick={onChangeCustomer}>{customerActionLabel}</DropdownItem>
									<DropdownItem onClick={() => setShowTicketActivity(show => !show)}>
										{activityToggleLabel}
									</DropdownItem>
								</DropdownContent>
							</Dropdown>
						</div>
						<div className={css(p.subject)}>{ticket.subject}</div>
						<div className={css(p.contactRow)}>
							<Avatar
								imgSrc={getSupportContactProfilePicSrc(ticket)}
								altText={contactLabel === '—' ? 'Contact' : contactLabel}
								fallbackText={getInitials(ticket.contactName, ticket.contactEmail)}
								styles={[p.contactAvatar]}
								fallbackStyles={[p.contactAvatar]}
							/>
							<span className={css(p.contactText)}>
								{contactLabel}
								{ticket.accountName ? ` · ${ticket.accountName}` : ''}
							</span>
						</div>
					</div>

					<div className={css(p.strip)}>
						<div className={css(p.stripControl)}>
							<Select
								value={ticket.assigneeId || ''}
								onValueChange={value =>
									assignTicket.mutate(
										value || null,
										feedback('Failed to assign ticket.', value ? onTicketLeftView : undefined)
									)
								}
							>
								<SelectTrigger aria-label='Ticket assignee' styles={p.stripTrigger}>
									<SelectValue
										placeholder='Unassigned'
										render={() => (
											<span data-slot='select-value'>
												{assigneeName ?? (ticket.assigneeId ? ticket.assigneeId : 'Unassigned')}
											</span>
										)}
									/>
								</SelectTrigger>
								<SelectContent>
									<SelectItem
										key='assignee-unassigned'
										value=''
										hint={agentTicketCountHint(agentTicketCounts, UNASSIGNED_COUNT_KEY)}
									>
										Unassigned
									</SelectItem>
									{teamMembers.map(agent => (
										<SelectItem
											key={agent.id}
											value={agent.id}
											hint={agentTicketCountHint(agentTicketCounts, agent.id)}
										>
											{agent.name}
										</SelectItem>
									))}
								</SelectContent>
							</Select>
						</div>
						<CategoryPicker
							categories={categories}
							selectedId={ticket.categoryId}
							onSelect={id => updateCategory.mutate(id || null, feedback('Failed to update category.'))}
							containerStyles={[p.stripControl]}
							triggerStyles={[p.stripTrigger]}
						/>
						<Button type='button' variant='link' styles={[p.detailsButton]} onClick={() => setOpenSheet('details')}>
							Details
							<ChevronDown width={14} height={14} strokeWidth={3} />
						</Button>
					</div>
				</>
			)}

			<ConversationPane
				ticketId={ticketId}
				forwardSignal={forwardSignal}
				autoOpenComposer={autoOpenComposer}
				targetThreadId={targetThreadId}
				onChangeCustomer={onChangeCustomer}
				onTicketClosed={onTicketLeftView}
			/>

			<Drawer
				open={openSheet === 'details'}
				onOpenChange={open => setOpenSheet(open ? 'details' : null)}
				showSwipeHandle
			>
				<DrawerContent styles={[p.sheet]}>
					<DrawerHeader styles={[p.sheetHeader]}>
						<DrawerTitle styles={[p.sheetTitle]}>Ticket details</DrawerTitle>
						<Button type='button' variant='link' styles={[p.sheetDone]} onClick={() => setOpenSheet(null)}>
							Done
						</Button>
					</DrawerHeader>
					<div className={css(p.sheetBody)}>
						<TicketDetailsBody
							ticketId={ticketId}
							onSelectTicket={onSelectTicket}
							onChangeCustomer={onChangeCustomer}
							onTicketLeftView={onTicketLeftView}
						/>
					</div>
				</DrawerContent>
			</Drawer>

			<Drawer
				open={openSheet === 'assistant'}
				onOpenChange={open => setOpenSheet(open ? 'assistant' : null)}
				showSwipeHandle
			>
				<DrawerContent styles={[p.assistantSheet]}>
					<DrawerHeader styles={[p.sheetHeader]}>
						<DrawerTitle styles={[p.sheetTitle]}>AI Assistant</DrawerTitle>
						<Button type='button' variant='link' styles={[p.sheetDone]} onClick={() => setOpenSheet(null)}>
							Done
						</Button>
					</DrawerHeader>
					{ticket?.accountId ? (
						<div className={css(p.assistantSheetBody)}>
							<AdminAppAssistantChat
								key={ticket.accountId}
								impersonationContext={{ account: { id: ticket.accountId } }}
							/>
						</div>
					) : (
						<div className={css(p.assistantEmpty)}>
							<SparklesIcon width={28} height={28} />
							<div>Connect this ticket to an account to use Assistant.</div>
						</div>
					)}
				</DrawerContent>
			</Drawer>

			{showReopenConfirm && (
				<ConfirmModal
					title='Reopen Ticket'
					message='Reopen this ticket and move it back to Active?'
					confirmLabel='Reopen'
					onConfirm={() => {
						setShowReopenConfirm(false);
						updateStatus.mutate(TicketStatus.Active, feedback('Failed to reopen ticket.'));
					}}
					onCancel={() => setShowReopenConfirm(false)}
				/>
			)}
		</div>
	);
};
